import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSources, type BillingRefusal } from "@onetasklist/shared";
import { useApi } from "@api/useApi";
import type { UpgradePromptViewModel } from "./UpgradePrompt.ViewModel.types";

/** The Account page holds the plan picker. */
const PLANS_PATH = "/account";

export const useUpgradePromptViewModel = (
  refusal: BillingRefusal | null,
  onDismiss: () => void,
): UpgradePromptViewModel => {
  const api = useApi();
  const navigate = useNavigate();
  const limit = refusal?.kind === "planLimit" ? refusal.limit : null;
  const sourceTypeId = limit?.sourceTypeId ?? null;
  const { data: sources } = useSources(api.sources, { enabled: !!sourceTypeId });

  const sourceName = useMemo(() => {
    if (!sourceTypeId) return null;
    const match = sources?.find((source) => source.id.toLowerCase() === sourceTypeId.toLowerCase());
    return match?.displayName ?? sourceTypeId;
  }, [sourceTypeId, sources]);

  const seePlans = useCallback(() => {
    onDismiss();
    navigate(PLANS_PATH);
  }, [navigate, onDismiss]);

  return {
    isOpen: refusal !== null,
    isPlanLimit: refusal?.kind === "planLimit",
    isUnavailable: refusal?.kind === "unavailable",
    limitKind: limit?.limit ?? null,
    planCode: limit?.planCode ?? null,
    max: limit?.max ?? null,
    sourceName,
    seePlans,
    dismiss: onDismiss,
  };
};
